// app/components/home/Map.tsx
"use client";

import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Location from "./Location.json";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
}));

export default function Map() {
  const t = useTranslations("Home");
  const locale = useLocale();


  const [pagePath, setPagePath] = useState<string>("/");

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
  }, []);

  // ---- default จาก i18n + static asset (ดีต่อ SEO/SSR) ----
  const defaultTitle = t("Map.title");
  const defaultMapImage = "/assets/home/map/map.png";

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [mapImage, setMapImage] = useState(defaultMapImage);

  // เปิดได้ "ทีละอันเดียว"
  const [activeId, setActiveId] = useState<number | null>(null);
  // ระบุว่า active เกิดจากการ "คลิก" (ปักค้าง) หรือไม่
  const [pinned, setPinned] = useState(false);

  // เมื่อ locale เปลี่ยน → reset กลับค่า default ก่อน
  useEffect(() => {
    setTitle(defaultTitle);
    setMapImage(defaultMapImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  useEffect(() => {
    if (!pagePath) return;

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback: เผื่อ row เก่าๆ ที่เก็บ selector เป็น [data-cms-key="..."]
          if (!key && e.selector) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) {
              key = match[1];
            }
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // text
        if (map["home.map.title"]) setTitle(map["home.map.title"]);

        // image
        if (map["home.map.image"]) setMapImage(map["home.map.image"]);
      } catch (err) {
        console.warn("[Map] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  // คลิก/แตะ: toggle เปิดค้างอันเดียว
  const handleToggleClick = (id: number) => {
    if (activeId === id && pinned) {
      // คลิกซ้ำตัวเดิม: ปิด
      setActiveId(null);
      setPinned(false);
    } else {
      // คลิกอันใหม่: เปิดอันใหม่ ปิดอันเก่า
      setActiveId(id);
      setPinned(true);
    }
  };

  // Hover: ใช้งานเฉพาะตอนยังไม่ปักค้าง
  const handleMouseEnter = (id: number) => {
    if (!pinned) setActiveId(id);
  };
  const handleMouseLeave = (id: number) => {
    if (!pinned && activeId === id) setActiveId(null);
  };

  // คลิกพื้นที่ว่าง → ปิดทั้งหมด
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest(".pulse")) return; // คลิกบนปุ่มเอง ไม่ต้องปิด
      setActiveId(null);
      setPinned(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <Box className="map-wrap">
      <Container className="container" sx={{ py: 8 }}>
        <Grid container>
          <Grid size={12} data-aos="fade-up">
            <Typography
              variant="h2"
              data-heading-tag="H2"
              data-aos="fade-up"
              // ✅ ผูกกับ CMS
              data-cms-key="home.map.title"
              sx={{
                mb: 5,
                whiteSpace: { xs: "normal", lg: "pre-line" },
                textAlign: "center"
              }}>
              {title}
            </Typography>

            <Box className="map-content" data-aos="zoom-in">
              <Image
                src={mapImage}
                width={1274}
                height={590}
                title="poonphol location"
                alt="poonphol location"
                className="img-fluid"
                // ✅ ให้แก้ URL แผนที่ได้จาก inline edit
                data-cms-key="home.map.image"
              />

              {Location.map((location) => {
                const isOpen = activeId === location.id;

                return (
                  <HtmlTooltip
                    key={location.id}
                    open={isOpen}
                    placement="top"
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    onClose={() => {
                      if (!pinned) setActiveId(null);
                    }}
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1
                        }}>
                        <Image
                          src={location.image}
                          width={29}
                          height={20}
                          title={location.name}
                          alt={location.name}
                        />
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600 }}
                        >
                          {locale === "en"
                            ? location.name
                            : location.name_cn}
                        </Typography>
                      </Box>
                    }
                  >
                    {/* layout เดิม 100% — แค่เติม class 'active' เมื่อเปิดอยู่ เพื่อเปลี่ยนสีแดง */}
                    <Box
                      className={`pulse${isOpen ? " active" : ""}`}
                      sx={{
                        left: `${location.left}%`,
                        top: `${location.top}%`,
                      }}
                      onMouseEnter={() => handleMouseEnter(location.id)}
                      onMouseLeave={() => handleMouseLeave(location.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleClick(location.id);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        handleToggleClick(location.id);
                      }}
                      role="button"
                      aria-label={`Location: ${
                        locale === "en"
                          ? location.name
                          : location.name_cn
                      }`}
                      aria-pressed={isOpen}
                    />
                  </HtmlTooltip>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}